import React, { useState } from 'react';
import styled from 'styled-components';
import MuiPopper from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import PropTypes from 'prop-types';

const PopperBase = styled(MuiPopper)`
  && {
    z-index: 1;

    &[x-placement*='bottom'] {
      margin-top: 0.5em;

      .arrow {
        top: 0;
        left: 0;
        margin-top: -0.9em;
        width: 3em;
        height: 1em;

        &::before {
          border-width: 0 1em 1em 1em;
          border-color: transparent transparent ${props => props.theme.palette.common.white}
            transparent;
        }
      }
    }

    &[x-placement*='top'] {
      margin-bottom: 0.5em;

      .arrow {
        left: 0;
        bottom: 0;
        margin-bottom: -0.9em;
        width: 3em;
        height: 1em;

        &::before {
          border-width: 1em 1em 0 1em;
          border-color: ${props => props.theme.palette.common.white} transparent transparent
            transparent;
        }
      }
    }

    &[x-placement*='right'] {
      margin-left: 0.5em;

      .arrow {
        left: 0;
        margin-left: -0.9em;
        height: 3em;
        width: 1em;

        &::before {
          border-width: 1em 1em 1em 0;
          border-color: transparent ${props => props.theme.palette.common.white} transparent
            transparent;
        }
      }
    }
    &[x-placement*='left'] {
      margin-right: 0.5em;

      .arrow {
        right: 0;
        margin-right: -0.9em;
        height: 3em;
        width: 1em;

        &::before {
          border-width: 1em 0 1em 1em;
          border-color: transparent transparent transparent
            ${props => props.theme.palette.common.white};
        }
      }
    }
  }
`;

const DropdownArrow = styled.span`
  position: absolute;
  font-size: 7px;
  width: 3em;
  height: 3em;

  &::before {
    content: '';
    margin: auto;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
  }
`;

const Popper = ({ open, disablePortal, anchorEl, position, id, handleClose, children }) => {
  const [arrowRef, setArrowRef] = useState(null);

  const handleArrowRef = node => setArrowRef(node);

  const getTransformOrigin = currentPlacement => {
    const replacements = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    };

    const regex = new RegExp(Object.keys(replacements).join('|'), 'gi');
    let transformOrigin = currentPlacement.replace(regex, match => replacements[match]);

    if (transformOrigin.includes('bottom') || transformOrigin.includes('top')) {
      transformOrigin = transformOrigin.replace('end', 'right').replace('start', 'left');
    } else if (transformOrigin.includes('left') || transformOrigin.includes('right')) {
      transformOrigin = transformOrigin.replace('end', 'bottom').replace('start', 'top');
    }

    return transformOrigin.replace('-', ' ');
  };

  return (
    <PopperBase
      open={open}
      anchorEl={anchorEl.current}
      transition
      placement={position}
      disablePortal={disablePortal}
      modifiers={{
        arrow: {
          enabled: true,
          element: arrowRef,
        },
      }}
    >
      {({ TransitionProps, placement }) => {
        const transformOrigin = getTransformOrigin(placement);

        return (
          <Grow {...TransitionProps} id={id} style={{ transformOrigin }}>
            <Paper style={{ minWidth: 150 }}>
              <DropdownArrow className="arrow" ref={handleArrowRef} />
              <ClickAwayListener onClickAway={handleClose}>
                <>{children}</>
              </ClickAwayListener>
            </Paper>
          </Grow>
        );
      }}
    </PopperBase>
  );
};

Popper.propTypes = {
  open: PropTypes.bool,
  disablePortal: PropTypes.bool,
  anchorEl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  position: PropTypes.oneOf([
    'bottom',
    'bottom-start',
    'bottom-end',
    'top',
    'top-start',
    'top-end',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end',
  ]),
};

Popper.defaultProps = {
  open: false,
  disablePortal: true,
  id: 'popper-instance',
  position: 'bottom-start',
};

export default Popper;
